import {
  useCallback,
  useState,
  type PropsWithChildren,
  type ReactNode,
  Fragment,
} from 'react';
import { createPortal } from 'react-dom';

import { OverlayStackingProvider } from './OverlayStackingProvider.js';
import { KeepMountedContext } from '../contexts/KeepMountedContext.js';

/**
 * Provider keeping a map of the children to be rendered in a `KeepMounted` component and
 * the element the children should be portalled to.
 * @internal
 */
export const KeepMountedContextProvider = ({ children }: PropsWithChildren) => {
  // Map of portals whose content should be kept mounted, even if the parent component unmounts.
  const [portals, setPortals] = useState(
    () => new Map<string, { children: ReactNode; element: HTMLElement }>(),
  );

  /** Get the element to portal the children to from the map of portals. */
  const getPortalElement = useCallback(
    (id: string, children: ReactNode) => {
      const keepPortal = portals.get(id);
      // If not yet available, create a portal container to portal the given
      // children to and store it in the set of portals.
      if (!keepPortal) {
        const element = document.createElement('div');
        setPortals(new Map(portals.set(id, { children, element })));

        return element;
      }

      // If we already have a portal, but the rendered children changed, update
      // the portals to render the updated children.
      if (keepPortal.children !== children) {
        setPortals(new Map(portals.set(id, { ...keepPortal, children })));
      }

      return keepPortal?.element;
    },
    [portals],
  );

  return (
    <KeepMountedContext.Provider
      value={{
        getPortalElement,
      }}
    >
      {children}
      <OverlayStackingProvider>
        {/* Iterate over all the stored portals and create the actual react portals. */}
        {Array.from(portals.entries()).map(([id, { children, element }]) => {
          return (
            <Fragment key={id}>
              {createPortal(children, element) as ReactNode}
            </Fragment>
          );
        })}
      </OverlayStackingProvider>
    </KeepMountedContext.Provider>
  );
};
